import { TransferStatusBadge } from '@/components/badges/TransferStatusBadge'
import { BaseDialog } from '@/components/BaseDialog'
import { DatePicker } from '@/components/DatePicker'
import { LogTimeline } from '@/components/LogTimeline'
import { QRScannerDialog, QRScannerDialogRef } from '@/components/QRScannerDialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { MultiSelect } from '@/components/ui/multi-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useGetItems } from '@/features/items/hooks/useItem'
import { getItemName } from '@/features/items/utils/items'
import { useGetConfigs } from '@/features/settings/hooks/useConfig'
import { AppConfigKey } from '@/features/settings/types/config'
import { useFileUploadField } from '@/hooks/useFileUpload'
import { useUI } from '@/hooks/useUI'
import { toUtcDateString } from '@/utils/date'
import { isApiError } from '@/utils/error'
import { showError, showSuccess } from '@/utils/toast'
import { CalendarDays, CheckCircle2, Loader2, ScanLine, XCircle } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useApproveTransferRequest,
  useGetTransferRequest,
  useGetTransferRequestLogs,
  useReceiveTransferRequest,
  useRejectTransferRequest,
  useScheduleTransferRequest,
} from '../hooks/useTransferRequest'
import { ScheduleInfo } from './transfer-dialog/ScheduleInfo'
import { TransferActionImages } from './transfer-dialog/TransferActionImages'
import { TransferImageList } from './transfer-dialog/TransferImageList'
import { TransferInfo } from './transfer-dialog/TransferInfo'

type HandleTransferDialogProps = {
  transferId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function HandleTransferDialog({ transferId, open, onOpenChange }: HandleTransferDialogProps) {
  const { t } = useTranslation()
  const { data: transfer, isLoading } = useGetTransferRequest(transferId, open)
  const { data: transferLogs = [], isLoading: isLoadingLogs } = useGetTransferRequestLogs(transferId, open)
  const { data: items = [], isLoading: isLoadingItems } = useGetItems(
    { productId: transfer?.productId, departmentId: transfer?.sourceDepartmentId ?? '', status: 'Available' },
    transfer?.status === 'Approved' && !!transfer?.productId && open,
  )
  const { data: configs, isLoading: isLoadingConfig } = useGetConfigs()

  const { canManageDepartment, isDepartmentMember } = useAuth()
  const { deliveryMethods } = useUI()
  const scannerRef = useRef<QRScannerDialogRef>(null)
  const [responseNote, setResponseNote] = useState('')
  const [itemIds, setItemIds] = useState<string[]>([])
  const [scheduledDate, setScheduledDate] = useState('')
  const [deliveryMethod, setDeliveryMethod] = useState('')
  const [handoffImageIds, setHandoffImageIds] = useState<string[]>([])
  const handoffImages = useFileUploadField({
    value: handoffImageIds,
    onChange: setHandoffImageIds,
    maxFiles: 4,
    onUploadError: error => showError(isApiError(error) ? error.message : t('common.uploadImageError')),
  })
  const approveTransferRequest = useApproveTransferRequest()
  const rejectTransferRequest = useRejectTransferRequest()
  const scheduleTransferRequest = useScheduleTransferRequest()
  // const deliverTransferRequest = useDeliverTransferRequest()
  // const markTransferPickupReady = useMarkTransferPickupReady()
  const receiveTransferRequest = useReceiveTransferRequest()

  const isSelectionComplete = itemIds.length >= (transfer?.quantityRequested ?? 0)
  const itemOptions = items.map(item => ({
    value: item.id,
    label: getItemName(item.productName, item.serialNumber),
    searchValue: `${item.productName || ''} ${item.serialNumber || ''}`,
    disabled: isSelectionComplete && !itemIds.includes(item.id),
  }))

  useEffect(() => {
    if (open) return

    setResponseNote('')
    setItemIds([])
    setScheduledDate('')
    setDeliveryMethod('')
    setHandoffImageIds([])
  }, [open])

  const isMutating =
    approveTransferRequest.isPending ||
    rejectTransferRequest.isPending ||
    scheduleTransferRequest.isPending ||
    // deliverTransferRequest.isPending ||
    // markTransferPickupReady.isPending ||
    receiveTransferRequest.isPending ||
    handoffImages.isUploading

  const runAction = async (action: () => Promise<unknown>, successMessage: string) => {
    try {
      await action()
      showSuccess(successMessage)
      setHandoffImageIds([])
      onOpenChange(false)
    } catch (error) {
      showError(isApiError(error) ? error.message : t('requests.actionError'))
    }
  }

  const handleScan = useCallback(
    (text: string) => {
      try {
        const url = new URL(text)
        const match = url.pathname.match(/\/scan\/equipment\/([^/]+)/)
        const itemId = match?.[1]

        if (!itemId) {
          showError(t('common.qrScanner.invalidQr'))
          return
        }

        const item = items.find(i => i.id === itemId)
        if (!item) {
          showError(t('common.qrScanner.equipmentNotFound'))
          return
        }

        setItemIds(prev => {
          if (prev.includes(itemId)) {
            showError(t('common.qrScanner.equipmentAlreadySelected'))
            return prev
          }

          if (prev.length >= (transfer?.quantityRequested ?? 0)) {
            showError(
              t('requests.equipmentRequired', {
                count: transfer?.quantityRequested ?? 0,
              }),
            )
            return prev
          }

          return [...prev, itemId]
        })
      } catch {
        showError(t('common.qrScanner.invalidQr'))
      }
    },
    [t],
  )

  const maxEquipment = Number.parseInt(configs?.[AppConfigKey.MaxEquipment] ?? '', 10) || 500
  const selectedDeliveryMethod = deliveryMethod || deliveryMethods?.[0]?.value || 'Deliver'
  const shouldShowImagesAfterInfo =
    transfer &&
    transfer.status !== 'Scheduled' &&
    transfer.status !== 'Delivering' &&
    transfer.status !== 'AwaitingReceive' &&
    transfer.status !== 'PickupReady'

  const canManage = canManageDepartment(transfer?.sourceDepartmentId)
  const canReceive = isDepartmentMember(transfer?.targetDepartmentId)
  return (
    <>
      <BaseDialog
        open={open}
        onOpenChange={onOpenChange}
        contentTestId="handle-transfer-dialog"
        header={
          <>
            {t('requests.requestTitle')} {transfer ? `#${transfer.id.slice(0, 8)}` : ''}
            {transfer && <TransferStatusBadge status={transfer.status} />}
          </>
        }
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            <Loader2 className="mr-2 size-4 animate-spin" />
            {t('common.loading')}
          </div>
        ) : transfer ? (
          <div className="flex flex-col gap-4 pt-2">
            <TransferInfo transfer={transfer} />

            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-foreground">{t('repairs.history')}</span>
              <LogTimeline entries={transferLogs} isLoading={isLoadingLogs} hiddenActions={['ItemStatusChanged']} />
            </div>

            {shouldShowImagesAfterInfo && <TransferImageList imageIds={transfer.imageIds ?? []} />}

            {transfer.status === 'Pending' && canManage && (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs">{t('requests.responseNote')}</Label>
                  <Textarea
                    data-testid="handle-transfer-response-note"
                    value={responseNote}
                    onChange={e => setResponseNote(e.target.value)}
                    className="bg-secondary/50 text-xs"
                    rows={2}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid="handle-transfer-approve"
                    className="flex-1 text-xs"
                    onClick={() =>
                      runAction(
                        () => approveTransferRequest.mutateAsync({ id: transfer.id, payload: { responseNote } }),
                        t('requests.approveSuccess'),
                      )
                    }
                    disabled={isMutating}
                  >
                    <CheckCircle2 className="mr-1 size-3" />
                    {t('requests.approve')}
                  </Button>
                  <Button
                    data-testid="handle-transfer-reject"
                    variant="destructive"
                    className="flex-1 text-xs"
                    onClick={() =>
                      runAction(
                        () => rejectTransferRequest.mutateAsync({ id: transfer.id, payload: { responseNote } }),
                        t('requests.rejectSuccess'),
                      )
                    }
                    disabled={isMutating}
                  >
                    <XCircle className="mr-1 size-3" />
                    {t('requests.reject')}
                  </Button>
                </div>
              </div>
            )}

            {transfer.status === 'Approved' && canManage && (
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex w-full flex-col gap-1.5 col-span-1 sm:col-span-2">
                    {transfer?.items.length == 0 && (
                      <>
                        <div className="flex justify-between gap-2">
                          <Label className="flex flex-col sm:flex-row items-end text-xs">
                            {t('common.placeholder.selectEquipment')} * {`(${itemIds.length})`}{' '}
                            <span className="text-destructive">
                              {t('requests.equipmentRequired', { count: transfer.quantityRequested })}
                            </span>
                          </Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => scannerRef.current?.start()}
                            disabled={isLoadingItems || isSelectionComplete}
                            title={t('common.qrScanner.scanButton')}
                          >
                            <ScanLine className="size-4" />
                          </Button>
                        </div>

                        <MultiSelect
                          options={itemOptions}
                          value={itemIds}
                          onValueChange={setItemIds}
                          placeholder={t('common.placeholder.selectEquipment')}
                          searchPlaceholder={t('common.placeholder.searchEquipment')}
                          emptyText={t('common.placeholder.noEquipment')}
                          testId="handle-transfer-items"
                        />
                      </>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-xs">{t('requests.scheduleDate')}</Label>
                    <DatePicker
                      testId="handle-transfer-scheduled-date"
                      value={scheduledDate}
                      onChange={setScheduledDate}
                      placeholder={t('requests.chooseScheduleDate')}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-xs">{t('requests.method')}</Label>
                    <Select value={selectedDeliveryMethod} onValueChange={setDeliveryMethod}>
                      <SelectTrigger
                        data-testid="handle-transfer-delivery-method"
                        className="h-9 w-full min-w-0 bg-secondary/50 text-xs [&>span]:truncate"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card text-foreground">
                        {deliveryMethods?.map(method => (
                          <SelectItem
                            data-testid={`handle-transfer-delivery-method-option-${method.value}`}
                            key={method.value}
                            value={method.value}
                          >
                            {method.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col col-span-1 sm:col-span-2 gap-1.5">
                    <TransferImageList imageIds={transfer.imageIds ?? []} />
                  </div>
                  <div className="flex flex-col col-span-1 sm:col-span-2 gap-1.5">
                    <TransferActionImages
                      label={t('requests.receiveImage')}
                      imageField={handoffImages}
                      testId="handle-transfer-schedule-images"
                    />
                  </div>
                </div>
                <Button
                  data-testid="handle-transfer-schedule"
                  className="text-xs"
                  onClick={() =>
                    handoffImageIds.length === 0
                      ? showError(t('requests.attachReceiveImage'))
                      : itemIds.length > maxEquipment
                        ? showError(t('requests.equipmentExceed', { count: maxEquipment }))
                        : runAction(
                            () =>
                              scheduleTransferRequest.mutateAsync({
                                id: transfer.id,
                                payload: {
                                  itemIds: itemIds,
                                  scheduledDate: toUtcDateString(scheduledDate),
                                  deliveryMethod: selectedDeliveryMethod,
                                  imageIds: handoffImageIds,
                                },
                              }),
                            t('requests.scheduleSuccess'),
                          )
                  }
                  disabled={!scheduledDate || isMutating}
                >
                  <CalendarDays className="mr-1 size-3" />
                  {t('requests.scheduleTransfer')}
                </Button>
              </div>
            )}

            {/* {transfer.status === 'Scheduled' && canManage && (
              <ScheduledTransferActions
                transfer={transfer}
                isMutating={isMutating}
                onDeliver={() =>
                  handoffImageIds.length === 0
                    ? showError(t('requests.attachImageBeforeDelivery'))
                    : runAction(
                        () =>
                          deliverTransferRequest.mutateAsync({
                            id: transfer.id,
                            payload: { imageIds: handoffImageIds },
                          }),
                        t('requests.deliverSuccess'),
                      )
                }
                onPickupReady={() =>
                  runAction(
                    () =>
                      markTransferPickupReady.mutateAsync({
                        id: transfer.id,
                        payload: { imageIds: handoffImageIds },
                      }),
                    t('requests.pickupReadySuccess'),
                  )
                }
                imageField={handoffImages}
                existingImageIds={transfer.imageIds ?? []}
              />
            )} */}

            {
              // transfer.status === 'Delivering' ||
              // transfer.status === 'AwaitingReceive' ||
              // transfer.status === 'PickupReady'
              transfer.status === 'Scheduled' && canReceive && (
                <div className="flex flex-col gap-3">
                  <ScheduleInfo transfer={transfer} />
                  <TransferImageList imageIds={transfer.imageIds ?? []} />
                  <TransferActionImages
                    label={t('requests.receiveImage')}
                    imageField={handoffImages}
                    testId="handle-transfer-receive-images"
                  />
                  <Button
                    data-testid="handle-transfer-receive"
                    className="text-xs"
                    onClick={() =>
                      handoffImageIds.length === 0
                        ? showError(t('requests.attachReceiveImage'))
                        : runAction(
                            () =>
                              receiveTransferRequest.mutateAsync({
                                id: transfer.id,
                                payload: { imageIds: handoffImageIds },
                              }),
                            t('requests.receiveSuccess'),
                          )
                    }
                    disabled={isMutating}
                  >
                    <CheckCircle2 className="mr-1 size-3" />
                    {t('requests.confirmReceived')}
                  </Button>
                </div>
              )
            }

            {transfer.status === 'Completed' && (
              <div className="rounded-lg border border-success/30 bg-success/5 p-3">
                <p className="flex items-center gap-2 text-xs text-success">
                  <CheckCircle2 className="size-3" />
                  {t('requests.completed')}
                </p>
                {transfer.sender && (
                  <p className="text-xs text-muted-foreground">
                    {t('requests.sender')}: {transfer.sender.name}
                  </p>
                )}
                {transfer.receiver && (
                  <p className="text-xs text-muted-foreground">
                    {t('requests.receiver')}: {transfer.receiver.name}
                  </p>
                )}
              </div>
            )}

            {transfer.status === 'Rejected' && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                <p className="flex items-center gap-2 text-xs text-destructive">
                  <XCircle className="size-3" />
                  {t('requests.rejected')}: {transfer.responseNote || '-'}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-muted-foreground">{t('requests.notFound')}</div>
        )}
      </BaseDialog>

      <QRScannerDialog ref={scannerRef} onScan={handleScan} />
    </>
  )
}
